import React from "react";
import { Checkbox, StateSelect, TextField } from "components";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import formatDate from "utils/formatDate";
import { isValidDate } from "utils/validDate";
import ButtonV2 from "elements/ButtonV2";

const PreviousTaxReturnAddress = () => {
	const {
		flowId,
		next,
		specialFieldsValue,
		setSpecialFieldsValue,
		previousAddressValue,
		previousAddress2Value,
		previousCityValue,
		previousStateValue,
		previousZipcodeValue,
	} = useExemptionsContext();
	const { details } = usePropertyContext();

	const handleSubmit = async () => {
		next();
	};

	const [differentAddressSelected, setDifferentAddressSelected] =
		React.useState(false);

	const previousAddressSelected = React.useMemo(() => {
		return [
			specialFieldsValue?.previous_tax_return_address?.address ===
				previousAddressValue,
			specialFieldsValue?.previous_tax_return_address?.address2 ===
				previousAddress2Value,
			specialFieldsValue?.previous_tax_return_address?.city ===
				previousCityValue,
			specialFieldsValue?.previous_tax_return_address?.state ===
				previousStateValue,
			specialFieldsValue?.previous_tax_return_address?.zip_code ===
				previousZipcodeValue,
		].every(v => !!v);
	}, [
		previousAddressValue,
		previousAddress2Value,
		previousCityValue,
		previousStateValue,
		previousZipcodeValue,
		specialFieldsValue,
	]);

	const currentAddressSelected = React.useMemo(() => {
		return [
			specialFieldsValue?.previous_tax_return_address?.address ===
				details?.address,
			specialFieldsValue?.previous_tax_return_address?.address2 ===
				details?.address2,
			specialFieldsValue?.previous_tax_return_address?.city === details?.city,
			specialFieldsValue?.previous_tax_return_address?.state === details?.state,
			specialFieldsValue?.previous_tax_return_address?.zip_code ===
				details?.zip_code,
		].every(v => !!v);
	}, [details, specialFieldsValue]);

	const buttonDisabled = React.useMemo(() => {
		return !(
			specialFieldsValue?.previous_tax_return_address?.address &&
			specialFieldsValue?.previous_tax_return_address?.city &&
			specialFieldsValue?.previous_tax_return_address?.state &&
			specialFieldsValue?.previous_tax_return_address?.zip_code
		);
	}, [flowId, specialFieldsValue]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>Address Used for Previous IRS Return</p>
				</div>
				<p className="exemption-question-card-subtitle sm">
					The county requires this information in order to approve the
					application.
				</p>

				<div className="exemptions-survey-rare-situations-checkbox-section mt-2">
					<Checkbox
						name={"previous_address"}
						label={`${previousAddressValue}${
							previousAddress2Value ? ` ${previousAddress2Value}` : ""
						}, ${previousCityValue}, ${previousStateValue} ${previousZipcodeValue}`}
						labelEl="p"
						checked={previousAddressSelected ?? false}
						onChange={() => {
							setDifferentAddressSelected(false);
							setSpecialFieldsValue({
								previous_tax_return_address: {
									address: previousAddressValue,
									address2: previousAddress2Value,
									city: previousCityValue,
									state: previousStateValue,
									zip_code: previousZipcodeValue,
								},
							});
						}}
					/>
					<Checkbox
						name={"current_address"}
						label={`${details?.address}${
							details?.address2 ? ` ${details?.address2}` : ""
						}, ${details?.city}, ${details?.state} ${details?.zip_code}`}
						labelEl="p"
						checked={currentAddressSelected ?? false}
						onChange={() => {
							setDifferentAddressSelected(false);
							setSpecialFieldsValue({
								previous_tax_return_address: {
									address: details?.address,
									address2: details?.address2,
									city: details?.city,
									state: details?.state,
									zip_code: details?.zip_code,
								},
							});
						}}
					/>
					<Checkbox
						name={"different_address"}
						label={"Different Address"}
						labelEl="p"
						checked={differentAddressSelected ?? false}
						onChange={() => {
							setDifferentAddressSelected(true);
							setSpecialFieldsValue({
								previous_tax_return_address: undefined,
							});
						}}
					/>
				</div>

				{differentAddressSelected && (
					<>
						<TextField
							required
							label="Street Address"
							placeholder="Street Address"
							value={
								specialFieldsValue?.previous_tax_return_address?.address ?? ""
							}
							containerClassName="mt-2"
							onChange={e => {
								setSpecialFieldsValue({
									previous_tax_return_address: {
										...specialFieldsValue?.previous_tax_return_address,
										address: e.target.value,
									},
								});
							}}
						/>
						<TextField
							placeholder="Apt/Suite/Unit"
							value={
								specialFieldsValue?.previous_tax_return_address?.address2 ?? ""
							}
							containerClassName="appeal-address-address2"
							onChange={e => {
								setSpecialFieldsValue({
									previous_tax_return_address: {
										...specialFieldsValue?.previous_tax_return_address,
										address: e.target.value,
									},
								});
							}}
						/>
						<div className="appeal-address-city-state-zip">
							<TextField
								required
								label="City"
								placeholder="City"
								value={
									specialFieldsValue?.previous_tax_return_address?.city ?? ""
								}
								containerClassName="appeal-address-city"
								onChange={e => {
									setSpecialFieldsValue({
										previous_tax_return_address: {
											...specialFieldsValue?.previous_tax_return_address,
											city: e.target.value,
										},
									});
								}}
							/>
							<StateSelect
								required
								label="State"
								placeholder="State"
								value={
									specialFieldsValue?.previous_tax_return_address?.state ?? ""
								}
								containerClassName="appeal-address-state mt-0"
								onChange={e => {
									setSpecialFieldsValue({
										previous_tax_return_address: {
											...specialFieldsValue?.previous_tax_return_address,
											state: e.target.value,
										},
									});
								}}
							/>
							<TextField
								required
								numeric
								label="Zip Code"
								placeholder="Zip Code"
								value={
									specialFieldsValue?.previous_tax_return_address?.zip_code ??
									""
								}
								minLength={5}
								maxLength={5}
								pattern="\d{5,5}(-\d{4,4})?"
								containerClassName="appeal-address-zip"
								onChange={e => {
									setSpecialFieldsValue({
										previous_tax_return_address: {
											...specialFieldsValue?.previous_tax_return_address,
											zip_code: e.target.value,
										},
									});
								}}
							/>
						</div>
					</>
				)}
			</div>
			<ButtonV2 size="large" disabled={buttonDisabled} type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export default PreviousTaxReturnAddress;
